/**
 * Lumiari Agent Chat Widget — Production v2 entry point.
 *
 * v2 is the SHIPPED bundle (`dist/widget.js`). The previous separate v2
 * bundle (`widget-v2.js`) is gone — Preact components, dispatcher,
 * realtime context, telemetry, and the storage service are all reachable
 * from this single entry point.
 *
 * Public API surface (unchanged from v1 to keep host pages working):
 *
 *   <script src="https://chat.lumiari.ai/widget.js"></script>
 *   <script>
 *     LumiariChat.init({
 *       widgetKey: 'wk_live_xxx',
 *       agentId: 'sales',
 *       apiUrl: 'https://chat-api.lumiari.ai',
 *       // ...
 *     });
 *   </script>
 *
 * Globals:
 *   - `window.LumiariChat` — the ONLY global. The pre-v3 legacy alias
 *     was removed in v3.0.0 (see `MIGRATION_V2.md`).
 */

import { ChatWidget } from './widget';
import type {
  HostTransport,
  LumiariChatHostGlobal,
  PreviewOptions,
  WidgetConfig,
} from './types';

// NOTE: v2 surface is intentionally NOT re-exported here. Hosts that
// want to compose Preact components / dispatcher / telemetry / realtime
// context attach those modules at build time from
// `@lumiari/chat-widget/v2` (see `dist/v2/`). Re-exporting from this
// file via `export * as v2` defeated tree-shaking and pulled the entire
// v2 module graph into every consumer of `widget.js`. The v2 modules
// remain part of the bundle for the legacy widget shell — anything
// the shell uses (v2 MessageList + ChatBubble + ProductGrid +
// ProductCard + CartPanel + ActionButton) is reachable via `widget.ts`.
// Heavy v2-only modules (ShopperLoginPanel, CheckoutEmbed Preact
// wrapper, telemetry, realtime-context, dispatcher) are accessible by
// hosts that import them directly from the `@lumiari/chat-widget/v2`
// entry point at build time. This keeps the shipped `widget.js` lean.

// Global widget instance
let widgetInstance: ChatWidget | null = null;
// Host transport of the current instance (`transport: 'host'` only).
let hostTx: HostTransport | undefined;

/**
 * Initialize the chat widget.
 *
 * `transport: 'host'` (Vesna playground) needs `widget-host.js` loaded
 * before this call: it validates the bridge (exact origin + nonce) and refuses a
 * widget key or shopper token — the parent owns credentials and the
 * network, and the widget never calls agent-api itself.
 */
function init(config: WidgetConfig): ChatWidget {
  let tx: HostTransport | undefined;
  if (config.transport === 'host') {
    const host = (window as unknown as { LumiariChatHost?: LumiariChatHostGlobal })
      .LumiariChatHost;
    if (!host) throw new Error('[LumiariChat] widget-host.js missing');
    // Throws on a bad origin / nonce, and on a widgetKey or shopperToken:
    // in host mode the parent owns credentials and the network.
    tx = host.create(config);
  } else if (!config.widgetKey && !config.storeId) {
    throw new Error('[LumiariChat] widgetKey (preferred) or storeId is required');
  }

  // Destroy existing instance if any
  destroy();

  // Create new instance
  widgetInstance = new ChatWidget(config, tx);
  hostTx = tx;
  tx && tx.bind(widgetInstance);
  return widgetInstance;
}

/**
 * Simulate the page the shopper is on (Vesna playground). Host transport
 * only: sets the scenario sent with every turn and starts a fresh
 * conversation. The parent page can do the same over the bridge.
 */
function preview(options: PreviewOptions): void {
  hostTx && hostTx.preview(options);
}

/**
 * Open the chat window.
 */
function open(): void {
  if (widgetInstance) {
    widgetInstance.open();
  } else {
    console.warn('[LumiariChat] Widget not initialized. Call init() first.');
  }
}

/**
 * Close the chat window.
 */
function close(): void {
  if (widgetInstance) {
    widgetInstance.close();
  }
}

/**
 * Toggle the chat window.
 */
function toggle(): void {
  if (widgetInstance) {
    widgetInstance.toggle();
  }
}

/**
 * Send a message programmatically.
 */
function sendMessage(text: string): void {
  if (widgetInstance) {
    widgetInstance.sendMessage(text);
  }
}

/**
 * Set user information.
 */
function setUser(user: { name?: string; email?: string; phone?: string }): void {
  if (widgetInstance) {
    widgetInstance.setUser(user);
  }
}

/**
 * Destroy the widget.
 */
function destroy(): void {
  if (hostTx) {
    hostTx.close();
    hostTx = undefined;
  }
  if (widgetInstance) {
    widgetInstance.destroy();
    widgetInstance = null;
  }
}

// Public API handle.
const LumiariChat = {
  init,
  open,
  close,
  toggle,
  sendMessage,
  setUser,
  destroy,
  preview,
  version: '3.6.3', // kept equal to package.json#version by index.globals.test.ts
};

// Make available globally — `LumiariChat` is the only global. The
// pre-v3 legacy alias was removed in v3.0.0 (see MIGRATION_V2.md).
if (typeof window !== 'undefined') {
  const w = window as unknown as { LumiariChat: typeof LumiariChat };
  w.LumiariChat = LumiariChat;
}

export default LumiariChat;
export { ChatWidget } from './widget';
export * from './types';
